'use client'

import { Select } from '@/components/ui/select'
import { useProductDetail } from '@/hooks/zustands/use-product-detail'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { formatPrice } from '@/lib/utils'
import { ProductPromotionGift } from '@/types/product-detail'
import Image from 'next/image'
import { useEffect, useState } from 'react'

interface ProductPromotionGiftItemProps {
  gift: ProductPromotionGift
  isSelected: boolean
  onSelect: () => void
}

export function ProductPromotionGiftItem({
  gift,
  isSelected,
  onSelect,
}: ProductPromotionGiftItemProps) {
  const { setGiftData } = useProductDetail()
  const [color, setColor] = useState(gift.colorOptions?.[0]?.value || '')
  const [size, setSize] = useState(gift.sizeOptions?.[0]?.value || '')

  useEffect(() => {
    const updateGiftOptionId = (color: string, size: string) => {
      const variant = gift.variants.find(
        (v) => v.option1 === color && v.option2 === size
      )

      if (variant) {
        setGiftData({
          id: gift.id,
          optionId: variant.id,
          price: gift.compare_price,
        })
      }
    }

    if (isSelected) {
      updateGiftOptionId(color, size)
    }
  }, [color, size, isSelected, gift, setGiftData])

  return (
    <div className='flex cursor-pointer' onClick={onSelect}>
      <div className='relative'>
        <Image
          src={ImageHelper.getImageUrl(gift.thumbnail)}
          alt={gift.display_name}
          className='h-full w-20 rounded-lg object-cover'
          width={80}
          height={120}
        />
        <Image
          src='/icons/icon-gift-secondary.svg'
          alt='gift'
          width={16}
          height={16}
          className='absolute left-1 top-1 h-4 w-4'
        />
      </div>

      <div className='flex-1 px-2'>
        <p className='mb-2 line-clamp-2 text-sm'>{gift.display_name}</p>

        <div className='my-2 flex space-x-2'>
          {gift.colorOptions && gift.colorOptions.length > 0 && (
            <Select
              options={gift.colorOptions || []}
              value={color}
              onValueChange={(val) => setColor(val)}
              className={{
                trigger: 'h-6 w-fit text-xs',
              }}
            />
          )}
          {gift.sizeOptions && gift.sizeOptions.length > 0 && (
            <Select
              options={gift.sizeOptions || []}
              value={size}
              onValueChange={(val) => setSize(val)}
              className={{
                trigger: 'h-6 w-fit text-xs',
              }}
            />
          )}
        </div>

        <div className='flex items-center space-x-2'>
          <span className='text-base font-bold'>
            {formatPrice(gift.regular_price)}
          </span>
          <span className='text-sm text-gray-400 line-through'>
            {formatPrice(gift.compare_price)}
          </span>
        </div>
      </div>
    </div>
  )
}
